import { MIN_STAKE } from '@star-snake/shared'

/**
 * SNAKE-30: pure state-machine that drives the PLAY button label, badge,
 * underline copy, and tap action.
 *
 * Five states from the GDD table:
 *
 *   trial          : freeGamesLeft > 0           → label "ИГРАТЬ", badge "БЕСПЛАТНО (N)"
 *   paid           : balance ≥ MIN_STAKE         → label "ИГРАТЬ", badge "1⭐ за вход"
 *   daily_ready    : 0 balance, daily available  → label "ИГРАТЬ" + 🎁 corner badge,
 *                                                   underline "Забери бонус — и в бой"
 *   need_deposit   : 0 balance, daily already claimed → label "ПОПОЛНИТЬ", badge "1⭐ = 1 вход"
 *   joining        : transient                   → label "ИГРАТЬ" (disabled), underline "Подключаемся…"
 *
 * Important: the disabled "Claim Daily First" state from SNAKE-22 is GONE.
 * When daily is available the button is fully clickable — its tap opens the
 * Daily Rewards screen with autoclaim. See action='claim_daily_then_play'.
 */

export type PlayButtonAction =
  | 'play'
  | 'claim_daily_then_play'
  | 'deposit'

export type PlayButtonKind = 'trial' | 'paid' | 'daily_ready' | 'need_deposit' | 'joining'

export interface PlayButtonInput {
  balance: number
  freeGamesLeft: number
  dailyAvailable: boolean
  joining: boolean
}

export interface PlayButtonModel {
  kind: PlayButtonKind
  action: PlayButtonAction
  primaryLabel: string
  badgeText: string | null
  /** Whether the badge text ends with "⭐" — drives renderer star-icon append. */
  badgeAppendsStar: boolean
  /** When true, show a small 🎁 sticker at the top-right corner of the button. */
  showGiftCorner: boolean
  /** Small text right below the button (NOT the smart hint — that's separate). */
  underline: string
  disabled: boolean
  variant: 'primary' | 'success'
}

export function buildPlayButtonModel(input: PlayButtonInput): PlayButtonModel {
  if (input.joining) {
    return {
      kind: 'joining',
      action: 'play',
      primaryLabel: 'ИГРАТЬ',
      badgeText: null,
      badgeAppendsStar: false,
      showGiftCorner: false,
      underline: 'Подключаемся…',
      disabled: true,
      variant: 'primary',
    }
  }

  if (input.freeGamesLeft > 0) {
    return {
      kind: 'trial',
      action: 'play',
      primaryLabel: 'ИГРАТЬ',
      badgeText: `БЕСПЛАТНО (${input.freeGamesLeft})`,
      badgeAppendsStar: false,
      showGiftCorner: false,
      underline: '',
      disabled: false,
      variant: 'primary',
    }
  }

  if (input.balance >= MIN_STAKE) {
    return {
      kind: 'paid',
      action: 'play',
      primaryLabel: 'ИГРАТЬ',
      // Trailing whitespace was a renderer hack — renderer now uses flex `gap`
      // between the text node and the cached star SVG, so plain "1" is right.
      badgeText: '1',
      badgeAppendsStar: true,
      showGiftCorner: false,
      underline: '',
      disabled: false,
      variant: 'primary',
    }
  }

  if (input.dailyAvailable) {
    // GDD: no Stars + daily available → button still ИГРАТЬ; tap opens daily
    // with autoclaim then comes back to play. The gift sticker advertises the
    // bonus without making the button look disabled.
    return {
      kind: 'daily_ready',
      action: 'claim_daily_then_play',
      primaryLabel: 'ИГРАТЬ',
      badgeText: null,
      badgeAppendsStar: false,
      showGiftCorner: true,
      underline: 'Забери бонус — и в бой',
      disabled: false,
      variant: 'primary',
    }
  }

  // No Stars, no trial, no daily → must top up.
  return {
    kind: 'need_deposit',
    action: 'deposit',
    primaryLabel: 'ПОПОЛНИТЬ',
    badgeText: '1⭐ = 1 вход',
    badgeAppendsStar: false,
    showGiftCorner: false,
    underline: '',
    disabled: false,
    variant: 'success',
  }
}
